import { useCallback, useEffect, useState } from 'react';
import {
  fetchCatSubscriptions,
  updateCatInfo as updateCatInfoRequest,
} from '../api/catSubscriptions';
import type { CatSubscription, UpdateCatInfoPayload } from '../types';

/**
 * Hook for fetching cat subscriptions for a specific customer
 */
export const useCatSubscriptions = (shopifyCustomerId: string) => {
  const [catSubscriptions, setCatSubscriptions] = useState<CatSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    if (!shopifyCustomerId) return;

    await Promise.resolve();
    setLoading(true);
    setError(null);

    try {
      const response = await fetchCatSubscriptions(shopifyCustomerId, page, perPage);
      setCatSubscriptions(response.items);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setCatSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [shopifyCustomerId, page, perPage]);

  useEffect(() => {
    void Promise.resolve().then(fetchData);
  }, [fetchData]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const updateCatInfo = async (catId: number, data: UpdateCatInfoPayload) => {
    const updatedCat = await updateCatInfoRequest(shopifyCustomerId, catId, data);
    setCatSubscriptions((current) =>
      current.map((catSubscription) =>
        catSubscription.id === updatedCat.id ? updatedCat : catSubscription,
      ),
    );
    return updatedCat;
  };

  return {
    catSubscriptions,
    loading,
    error,
    page,
    total,
    perPage,
    totalPages: Math.ceil(total / perPage),
    handlePageChange,
    handlePerPageChange,
    updateCatInfo,
    refetch: fetchData,
  };
};
